import {useState} from 'react';
import {toast} from '@/components/Toast';
import {useRouter} from 'next/router';
import {hubSpotPostRequest} from '@/Services/ApiService';
import {ApiError} from '@/Services/ApiError';
import {useIpGeolocation} from './useIpGeolocation';
import {IpData} from './ipGeolocationCache';
import type {FormHelpers} from './useForm';
import {sourcePageFields} from '@/util/sourcePage';
import {hubspotUtkContext} from '@/util/hubspotTracking';

// HubSpot 400 errors can come back as:
//   { errors: [{ message: "...", errorType: "..." }] }       ← validation errors
//   { status: "error", message: "...", error: "..." }        ← API-level errors
// responseData is the raw server body stored on ApiError by the interceptor.
export function extractHubSpotError(error: unknown): string | undefined {
    const body = error instanceof ApiError ? (error.responseData as Record<string, unknown> | undefined) : undefined;
    if (!body) return error instanceof Error ? error.message : undefined;

    // Array-style validation errors
    const errors = body.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        return (errors[0] as Record<string, unknown>)?.message as string | undefined;
    }

    // Object-keyed errors (legacy HubSpot format)
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        const firstKey = Object.keys(errors)[0];
        const val = (errors as Record<string, unknown>)[firstKey];
        return Array.isArray(val) ? (val[0] as string) : undefined;
    }

    return (body.message as string) ?? undefined;
}

export interface UseContactFormOptions<TValues = Record<string, unknown>> {
    /** HubSpot form path: `${PORTAL_ID}/${FORM_ID}` */
    formPath: string;
    /** Builds the key/value map sent to HubSpot as the fields array */
    buildFields: (values: TValues, ip: IpData) => Record<string, string>;
    /** Called after a successful HubSpot submission */
    onSuccess: (values: TValues, helpers: {resetForm: () => void}) => void;
    /** Called on submission error. Defaults to toast.error with the HubSpot error message. */
    onError?: (error: unknown) => void;
}

export function useContactForm<TValues = Record<string, unknown>>({
    formPath,
    buildFields,
    onSuccess,
    onError
}: UseContactFormOptions<TValues>) {
    const router = useRouter();
    const {ipLocation, defaultCountry} = useIpGeolocation();
    const [loading, setLoading] = useState(false);
    const [verified, setVerified] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onCaptchaChange = (_value: string | null) => setVerified(true);
    const onCaptchaExpired = () => setVerified(false);

    const handleSubmit = async (values: TValues, helpers: FormHelpers): Promise<void> => {
        if (!verified) return;

        const fields = [
            ...Object.entries(buildFields(values, ipLocation ?? {country_code: 'in', city: '', country_name: ''})).map(([name, value]) => ({
                name,
                value
            })),
            ...sourcePageFields()
        ];

        const contactData = {
            fields,
            context: {
                // Links the submission to the visitor's tracked page views (first page seen, journey).
                ...hubspotUtkContext(),
                pageUri: process.env.NEXT_PUBLIC_DOMAIN + router.asPath,
                pageName: typeof document === 'undefined' ? '' : document.title
            }
        };

        setLoading(true);
        try {
            await hubSpotPostRequest(formPath, contactData);
            onSuccess(values, {resetForm: helpers.resetForm});
        } catch (error: unknown) {
            if (onError) {
                onError(error);
            } else {
                const msg = extractHubSpotError(error);
                toast.error(msg ?? 'Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        verified,
        onCaptchaChange,
        onCaptchaExpired,
        loading,
        ipLocation,
        defaultCountry,
        handleSubmit
    };
}
